import React, { useId, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';

import { useCrossDockOptions } from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { Combobox } from 'shadcn/components/ui/Combobox';

/**
 * Renders a cross dock picker select component.
 *
 * @param {Object} props Component props.
 * @param {string|number|Object} [props.value] Selected cross dock ID or cross dock object.
 * @param {Function} [props.onValueChange] Change handler invoked with the selected cross dock ID.
 * @param {string} [props.label='Cross Dock'] Visible label rendered above the picker.
 * @param {boolean} [props.showLabel=true] Shows the label when true, hides it when false.
 * @returns {JSX.Element} Cross dock picker component.
 */
export function CrossDockPicker({ label = 'Cross Dock', onValueChange, showLabel = true, value }) {
    const labelId = useId();
    const { data: crossDockOptions = [], isFetching, isLoading } = useCrossDockOptions();

    // Options are already formatted as {value, label} from useCrossDockOptions
    // Add "None" option at the beginning to allow clearing the selection
    const options = useMemo(() => {
        const crossDockOptionsList = crossDockOptions.map((option) => ({
            value: String(option.value),
            label: option.label,
        }));
        return [{ value: '', label: 'None' }, ...crossDockOptionsList];
    }, [crossDockOptions]);

    // Extract ID from value (handles ID or cross dock object)
    const extractId = (val) => {
        if (val == null) return null;
        if (typeof val === 'object' && val.id != null) {
            return String(val.id);
        }
        return String(val);
    };

    // Derive selected value as ID string (for Combobox)
    const selectedValue = useMemo(() => {
        if (!value) return '';
        return extractId(value) ?? '';
    }, [value]);

    // Handle select change - return cross dock ID or empty string for "None"
    const handleValueChange = (newValue) => {
        if (onValueChange) {
            if (newValue === '' || !newValue) {
                // "None" selected - send empty string
                onValueChange('');
            } else {
                const crossDockId = Number(newValue);
                onValueChange(crossDockId);
            }
        }
    };

    // Show loading spinner when fetching and no data (or data is empty placeholder)
    const showLoading = isLoading || (isFetching && crossDockOptions.length === 0);

    if (showLoading) {
        return (
            <div className="flex flex-col gap-2">
                {showLabel && label && (
                    <span
                        id={labelId}
                        className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                        {label}
                    </span>
                )}
                <div className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading cross docks…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {showLabel && label && (
                <span
                    id={labelId}
                    className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                    {label}
                </span>
            )}
            <Combobox
                id={labelId}
                options={options}
                value={selectedValue}
                handleSelect={handleValueChange}
                placeholder="Select cross dock"
                disabled={!options.length || showLoading}
                popoverWidth="trigger"
                className="w-full"
                commandProps={{
                    filter: (value, search) => {
                        // Filter by label (cross dock name)
                        const option = options.find((opt) => String(opt.value) === String(value));
                        if (!option) return false;
                        // Allow "None" to be searchable
                        if (option.value === '') {
                            return 'none'.includes(search.toLowerCase());
                        }
                        return option.label.toLowerCase().includes(search.toLowerCase());
                    },
                }}
            />
        </div>
    );
}

CrossDockPicker.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    onValueChange: PropTypes.func,
    showLabel: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
};

CrossDockPicker.defaultProps = {
    value: undefined,
    onValueChange: undefined,
    label: 'Cross Dock',
    showLabel: true,
};

CrossDockPicker.displayName = 'CrossDockPicker';
